import { Fragment, useEffect, useState } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import {
  Bars3BottomLeftIcon,
  BellIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  UserGroupIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';

import { Link, Outlet, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useNavigate, NavLink } from 'react-router-dom';
import { useGetPartnerQuery } from 'features/PartnerSlice';
// import SearchHeader from './SearchHeader';

const sideBarConfig = [
  {
    path: '/',
    text: 'Home',
    icon: HomeIcon,
    isDisabled: false,
  },
  {
    path: '/cycle',
    text: 'Placement Cycles',
    icon: BuildingOfficeIcon,
    isDisabled: false,
  },
  {
    path: '/students',
    text: 'Students',
    icon: UsersIcon,
    isDisabled: false,
  },

  {
    path: '/employers',
    text: 'Employers',
    icon: FolderIcon,
    isDisabled: false,
  },
  {
    path: '/reports',
    text: 'Reports',
    icon: ChartBarIcon,
    isDisabled: false,
  },
  {
    path: '/announcements',
    text: 'Announcements',
    icon: SpeakerWaveIcon,
    isDisabled: false,
  },
];

const userNavigation = [
  { name: 'Your Profile', href: '#' },
  // { name: 'Settings', href: '#' },
];

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ');
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useGetPartnerQuery();
  const roles = data?.roles || [];
  const isAdmin = roles.includes('Partner Admin');

  const location = useLocation();

  useEffect(() => {
    if (
      data &&
      !data?.partnerId?.onBoarding &&
      !location.pathname.includes('onboarding')
    ) {
      navigate('/onboarding');
    }
  }, [data, location.pathname, navigate]);

  const image = data?.imageUrl
    ? `https://api.belong.education/api/upload/${data?.imageUrl}`
    : 'https://res.cloudinary.com/belong/image/upload/v1675236744/test/Upload_Placement_Profile_Hover_nmjf4j.svg';
  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as='div'
            className='relative z-40 md:hidden'
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter='transition-opacity ease-linear duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='transition-opacity ease-linear duration-300'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-gray-600 bg-opacity-75' />
            </Transition.Child>

            <div className='fixed inset-0 z-40 flex'>
              <Transition.Child
                as={Fragment}
                enter='transition ease-in-out duration-300 transform'
                enterFrom='-translate-x-full'
                enterTo='translate-x-0'
                leave='transition ease-in-out duration-300 transform'
                leaveFrom='translate-x-0'
                leaveTo='-translate-x-full'
              >
                <Dialog.Panel className='relative flex w-full max-w-xs flex-1 flex-col bg-white pt-5 pb-4'>
                  <Transition.Child
                    as={Fragment}
                    enter='ease-in-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in-out duration-300'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  >
                    <div className='absolute top-0 right-0 -mr-12 pt-2'>
                      <button
                        type='button'
                        className='ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className='sr-only'>Close sidebar</span>
                        <XMarkIcon
                          className='h-6 w-6 text-white'
                          aria-hidden='true'
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className='flex flex-shrink-0 items-center px-4'>
                    <img
                      className='h-8 w-auto'
                      src='https://res.cloudinary.com/belong/image/upload/v1650621217/uploaded_resources/logo_gqty7l.png'
                      alt='Your University'
                    />
                  </div>
                  <div className='mt-5 h-0 flex-1 overflow-y-auto'>
                    <nav className='space-y-1 px-2'>
                      {sideBarConfig.map((element) => {
                        return (
                          <div key={element.path}>
                            <NavLink
                              className={({ isActive }) =>
                                isActive
                                  ? 'bg-primary-lightest rounded-full w-full inline-block text-gray-500 px-6 py-4'
                                  : element.isDisabled
                                  ? 'rounded-full w-full inline-block px-6 py-4 pointer-events-none'
                                  : 'hover:bg-primary-lightest transition-all ease-in-out duration-300 text-gray-500 rounded-full w-full inline-block px-6 py-4'
                              }
                              to={element.path}
                            >
                              <div className='flex justify-between'>
                                <div className='flex items-center space-x-4 '>
                                  <element.icon
                                    className='text-gray-500 mr-3 flex-shrink-0 h-6 w-6'
                                    aria-hidden='true'
                                  />
                                  <span className='font-semibold text-textColor-main/60'>
                                    {element.text}
                                  </span>
                                </div>
                              </div>
                            </NavLink>
                          </div>
                        );
                      })}
                      {isAdmin && (
                        <NavLink
                          className={({ isActive }) =>
                            isActive
                              ? 'bg-primary-lightest rounded-full w-full inline-block text-gray-500 px-6 py-4'
                              : 'hover:bg-primary-lightest transition-all ease-in-out duration-300 text-gray-500 rounded-full w-full inline-block px-6 py-4'
                          }
                          to='/team'
                        >
                          <div className='flex justify-between'>
                            <div className='flex items-center space-x-4 '>
                              <UserGroupIcon
                                className='text-gray-500 mr-3 flex-shrink-0 h-6 w-6'
                                aria-hidden='true'
                              />
                              <span className='font-semibold text-textColor-main/60'>
                                Team Management
                              </span>
                            </div>
                          </div>
                        </NavLink>
                      )}
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className='w-14 flex-shrink-0' aria-hidden='true'>
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className='hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col'>
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className='flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5'>
            <div className='flex flex-shrink-0 items-center px-4'>
              <img
                className='h-8 w-auto'
                src='https://res.cloudinary.com/belong/image/upload/v1650621217/uploaded_resources/logo_gqty7l.png'
                alt='Your Company'
              />
            </div>
            <div className='mt-5 flex flex-grow flex-col'>
              <nav className='space-y-1 px-2'>
                {sideBarConfig.map((element) => {
                  return (
                    <div key={element.path}>
                      <NavLink
                        className={({ isActive }) =>
                          isActive
                            ? 'bg-primary-lightest rounded-full w-full inline-block text-gray-500 px-6 py-4'
                            : element.isDisabled
                            ? 'rounded-full w-full inline-block px-6 py-4 pointer-events-none'
                            : 'hover:bg-primary-lightest transition-all ease-in-out duration-300 text-gray-500 rounded-full w-full inline-block px-6 py-4'
                        }
                        to={element.path}
                      >
                        <div className='flex justify-between'>
                          <div className='flex items-center space-x-4 '>
                            <element.icon
                              className='text-gray-500 mr-3 flex-shrink-0 h-6 w-6'
                              aria-hidden='true'
                            />
                            <span className='font-semibold text-textColor-main/60'>
                              {element.text}
                            </span>
                          </div>
                        </div>
                      </NavLink>
                    </div>
                  );
                })}
                {isAdmin && (
                  <NavLink
                    className={({ isActive }) =>
                      isActive
                        ? 'bg-primary-lightest rounded-full w-full inline-block text-gray-500 px-6 py-4'
                        : 'hover:bg-primary-lightest transition-all ease-in-out duration-300 text-gray-500 rounded-full w-full inline-block px-6 py-4'
                    }
                    to='/team'
                  >
                    <div className='flex justify-between'>
                      <div className='flex items-center space-x-4 '>
                        <UserGroupIcon
                          className='text-gray-500 mr-3 flex-shrink-0 h-6 w-6'
                          aria-hidden='true'
                        />
                        <span className='font-semibold text-textColor-main/60'>
                          Team Management
                        </span>
                      </div>
                    </div>
                  </NavLink>
                )}
              </nav>
            </div>
          </div>
        </div>
        <div className='flex flex-1 flex-col md:pl-64'>
          <div className='sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow'>
            <button
              type='button'
              className='border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden'
              onClick={() => setSidebarOpen(true)}
            >
              <span className='sr-only'>Open sidebar</span>
              <Bars3BottomLeftIcon className='h-6 w-6' aria-hidden='true' />
            </button>
            <div className='flex flex-1 justify-between px-4'>
              <div className='flex flex-1'>{/* <SearchHeader /> */}</div>
              <div className='ml-4 flex items-center md:ml-6'>
                <button
                  type='button'
                  className='rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                >
                  <span className='sr-only'>View notifications</span>
                  <BellIcon className='h-6 w-6 hidden' aria-hidden='true' />
                </button>

                {/* Profile dropdown */}
                <Menu as='div' className='relative ml-3'>
                  <div>
                    <Menu.Button className='flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'>
                      <span className='sr-only'>Open user menu</span>
                      <img
                        className='h-8 w-8 rounded-full'
                        src={image}
                        alt=''
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter='transition ease-out duration-100'
                    enterFrom='transform opacity-0 scale-95'
                    enterTo='transform opacity-100 scale-100'
                    leave='transition ease-in duration-75'
                    leaveFrom='transform opacity-100 scale-100'
                    leaveTo='transform opacity-0 scale-95'
                  >
                    <Menu.Items className='absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
                      {userNavigation.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <Link
                              to={item.href}
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              {item.name}
                            </Link>
                          )}
                        </Menu.Item>
                      ))}
                      <Menu.Item>
                        <button
                          className='block px-4 py-2 text-sm text-gray-700'
                          onClick={async () => {
                            await Auth.signOut({ global: true });
                            navigate('/login', { replace: true });
                          }}
                        >
                          Sign out
                        </button>
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <main className='flex-1'>
            <div className='py-6'>
              <div className='mx-auto max-w-7xl px-4 sm:px-6 md:px-8'>
                {/* Replace with your content */}
                {<Outlet />}
                {/* /End replace */}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
